import { html, LitElement } from 'lit';
import { connect } from 'pwa-helpers';

import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import {
  debounceTime, filter, fromEvent, take, tap,
} from 'rxjs';
import { loadMapGeoJSON } from './redux/ducks/actions/data.js';
import { store } from './redux/store.js';
import {
  mouseEnterFeature, mouseLeaveFeature,
  resetFeatureSelection,
  selectFeature,
} from './redux/ducks/actions/feature.js';
import {
  isLoading,
  getMouseOverFeatureId,
  getSelectedFeatureId,
} from './redux/ducks/selectors/index.js';

class ToujouSolrSearchMap extends connect(store)(LitElement) {
  static get is() {
    return 'toujou-solr-search-map';
  }

  static get properties() {
    return {
      isLoading: {
        type: Boolean,
        reflect: true,
        attribute: 'is-loading',
      },
      isMobile: {
        type: Boolean,
        reflect: true,
      },
      reducedMotion: {
        type: Boolean,
        attribute: 'reduced-motion',
      },
      _geoJsonData: {
        type: Object,
      },
      _map: {
        type: Object,
      },
      _popupFeature: {
        type: String,
      },
      _selectedFeatureId: {
        type: String,
      },
      _mouseOverFeatureId: {
        type: String,
      },
      geoJsonUrl: {
        type: String,
      },
      teaserUrl: {
        type: String,
        attribute: 'teaser-url',
      },
      featureGeoJsonUrl: {
        type: String,
        attribute: 'feature-geojson-url',
      },
      accessToken: {
        type: String,
        attribute: 'access-token',
      },
      mapStyle: {
        type: String,
        attribute: 'map-style',
      },
    };
  }

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();

    this._fixLayersPosition = this._fixLayersPosition.bind(this);
    this.geoJsonUrl = '';
    this.teaserUrl = '';
    this._geoJsonData = null;
    this._popupFeature = null;
    this._popupCoordinates = null;
    this._selectedFeatureId = null;
    this._mouseOverFeatureId = null;
    this._poupGeoJson = null;
    this.accessToken = null;
    this.iconImage = 'marker-15';
    this.reducedMotion = false;
    this._mapPadding = {
      top: 16,
      bottom: 16,
      left: 16,
      right: 16,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this._initStyles();
  }

  disconnectedCallback() {
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
    }
    if (this.resultChangedSubscription) {
      this.resultChangedSubscription.unsubscribe();
    }

    super.disconnectedCallback();
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);
    if (_changedProperties.has('_map')) {
      this._init();
    }

    if (!this._map) {
      return;
    }
    if (_changedProperties.has('_selectedFeatureId') || _changedProperties.has('_mouseOverFeatureId')) {
      this._updateHighlightedMapFeature();
    }
  }

  stateChanged(state) {
    this._state = state;
    this.isLoading = isLoading(this._state);
    this._geoJsonData = this._state.data.geojson;
    this._popupFeature = this._state.feature.popupHtml;
    this._popupCoordinates = this._state.feature.coordinates;
    this._poupGeoJson = this._state.feature.geoJson;

    this._selectedFeatureId = getSelectedFeatureId(this._state);
    this._mouseOverFeatureId = getMouseOverFeatureId(this._state);
  }

  _initStyles() {
    const bodyStyles = window.getComputedStyle(this);
    this._mapStyles = {
      markerBgColor: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-marker-background-color',
      ),
      markerRadius: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-marker-background-radius',
      ),
      markerBorderWidth: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-marker-border-width',
      ),
      markerBorderColor: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-marker-border-color',
      ),
      iconColor: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-marker-icon-color',
      ),
      clusterBgColor: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-cluster-background-color',
      ),
      clusterBorderWidth: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-cluster-border-width',
      ),
      clusterBorderColor: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-cluster-border-color',
      ),
      clusterTextColor: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-cluster-text-color',
      ),
      clusterTextSize: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-cluster-text-size',
      ),
      clusterRadius: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-cluster-radius',
      ),
      clusterMaxZoom: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-cluster-max-zoom',
      ),
      lineColor: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-line-color',
      ),
      lineWidth: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-line-width',
      ),
      iconImage: bodyStyles.getPropertyValue(
        '--toujou-solr-search-map-icon-image',
      ),
      markerBgColorHover: bodyStyles.getPropertyValue('--toujou-solr-search-map-marker-background-color-hover'),
    };

    const breakpoint = bodyStyles.getPropertyValue('--toujou-solr-search-breakpoint');

    const mediaQueryList = window.matchMedia(`(max-width: ${breakpoint})`);
    this.isMobile = mediaQueryList.matches;
    mediaQueryList.addEventListener('change', () => {
      this.isMobile = mediaQueryList.matches;
    });
  }

  _init() {
    if (!this._map) return;
    this._fetchData();
  }

  render() {
    return html`
      <div class="loading-bar"></div>
      <toujou-map
        @toujou-map-ready="${this._onMapReady}"
        access-token="${this.accessToken}"
        map-style="${this.mapStyle}"
      >
        <!--  LINES LAYER -->
        <toujou-map-layer
          layer-id="featureGeoJson"
          source="geoJsonLines"
          type="line"
          layout='{
              "line-join": "round",
              "line-cap": "round"
            }'
          paint='{"line-color":"${this._mapStyles.lineColor}","line-width":${this._mapStyles.lineWidth}}'
          filter='["==", "$type", "LineString"]'
        ></toujou-map-layer>
        <toujou-map-layer
          layer-id="marker"
          source="geoJsonData"
          type="circle"
          filter='["!", ["has", "point_count"]]'
          paint ='{
            "circle-radius": ${this._mapStyles.markerRadius},
            "circle-color": "${this._mapStyles.markerBgColor}",
            "circle-stroke-width": ${this._mapStyles.markerBorderWidth},
            "circle-stroke-color": "${this._mapStyles.markerBorderColor}"
          }'
        ></toujou-map-layer>
        <toujou-map-layer
          layer-id="marker-icon"
          source="geoJsonData"
          type="symbol"
          layout='{"icon-image": ${this._mapStyles.iconImage}}'
          paint='{"icon-color": "${this._mapStyles.iconColor}"}'
          filter='["!", ["has", "point_count"]]'
        ></toujou-map-layer>
        <toujou-map-layer
          layer-id="clusters"
          source="geoJsonData"
          type="circle"
          filter='["has", "point_count"]'
          paint='{
            "circle-radius": [ "step", ["get", "point_count"], 12, 3, 16, 5, 20, 10, 25, 25, 30 ],
            "circle-color": "${this._mapStyles.clusterBgColor}",
            "circle-stroke-width": ${this._mapStyles.clusterBorderWidth},
            "circle-stroke-color": "${this._mapStyles.clusterBorderColor}"
          }'
        ></toujou-map-layer>
        <toujou-map-layer
          layer-id="cluster-count"
          source="geoJsonData"
          type="symbol"
          filter='["has", "point_count"]'
          layout='{ "text-field": "{point_count_abbreviated}", "text-size": ${this._mapStyles.clusterTextSize} }'
          paint='{ "text-color": "${this._mapStyles.clusterTextColor}" }'
        ></toujou-map-layer>
        <toujou-map-geojson
          id="geoJsonSourcePolygons"
          cluster
          cluster-radius="${this._mapStyles.clusterRadius}"
          cluster-max-zoom="${this._mapStyles.clusterMaxZoom}"
          .sourceData='${this._geoJsonData}'
          source-id="geoJsonData">
        </toujou-map-geojson>
        <toujou-map-geojson
          id="geoJsonLines"
          .sourceData='${this._poupGeoJson}'
          source-id="geoJsonLines">
        </toujou-map-geojson>
        ${!this.isMobile && this._popupFeature && this._popupCoordinates ? html`
          <toujou-map-popup .coordinates="${this._popupCoordinates}">
            ${unsafeHTML(this._popupFeature)}
          </toujou-map-popup>
        ` : ''}
      </toujou-map>
      ${this.isMobile && this._popupFeature && this._popupCoordinates ? html`
        <div class="toujou-map__fixed-popup">
          ${unsafeHTML(this._popupFeature)}
        </div>
        ` : ''}
    `;
  }

  /**
   * We listen for a custom 'toujou-map-ready' event and then set the _map property and hover listeners
   *
   * @param event
   * @private
   */
  _onMapReady(event) {
    this._map = event.detail;
    this.addEventListener('toujou-map-layer-added', layerAddedEvent => this._fixLayersPosition(layerAddedEvent));
    this._map.setPadding(this._mapPadding);

    this.clickSubscription = fromEvent(this._map, 'click').pipe(
      tap((mapClickEvent) => {
        store.dispatch(resetFeatureSelection());

        const clickedFeature = this._map.queryRenderedFeatures(mapClickEvent.point)[0];
        if (!clickedFeature) {
          return;
        }

        if (/cluster/.test(clickedFeature.layer.id)) {
          const clusterId = clickedFeature.properties.cluster_id;
          this._map.getSource('geoJsonData').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) {
              return;
            }
            this._map.easeTo({
              center: clickedFeature.geometry.coordinates,
              zoom,
              animate: !this.reducedMotion,
            });
          });

          return;
        }
        store.dispatch(mouseLeaveFeature());
        store.dispatch(resetFeatureSelection());

        this._map.panTo(mapClickEvent.lngLat);
        store.dispatch(selectFeature(
          clickedFeature.properties,
          this.teaserUrl,
          this.featureGeoJsonUrl,
          mapClickEvent.lngLat,
        ));
      }),
    ).subscribe();

    this._map.on('mouseenter', 'clusters', () => { this._map.getCanvas().style.cursor = 'pointer'; });
    this._map.on('mouseleave', 'clusters', () => { this._map.getCanvas().style.cursor = ''; });

    this._map.on('mouseenter', 'marker', (e) => {
      this._map.getCanvas().style.cursor = 'pointer';
      store.dispatch(mouseEnterFeature(e.features[0].properties));
    });
    this._map.on('mouseleave', 'marker', () => {
      this._map.getCanvas().style.cursor = '';
      store.dispatch(mouseLeaveFeature());
    });

    this.resultChangedSubscription = fromEvent(window, 'toujou-solr-search-new-ajax-result').pipe(
      tap(() => store.dispatch(resetFeatureSelection())),
      tap(() => this._fetchData()),
    ).subscribe();
  }

  /**
   * Fetch current data for the current visible area
   *
   * @private
   */
  _fetchData() {
    // add filter params
    const url = `${this.geoJsonUrl}${document.location.search}`;
    store.dispatch(loadMapGeoJSON(url));

    this.sourceLoadedSubscription = fromEvent(this.querySelector('toujou-map'), 'toujou-map-source-data-loaded').pipe(
      filter(() => this.isLoading === false),
      filter(() => this._map),
      filter(() => this._state.data.geojson),
      take(1),
      debounceTime(100),
      tap(() => {
        if (this._state.data.geojson.features.length === 0) {
          return;
        }

        // eslint-disable-next-line no-undef
        const geoJsonBounds = new maplibregl.LngLatBounds();
        this._state.data.geojson.features.forEach((feature) => {
          geoJsonBounds.extend(feature.geometry.coordinates);
        });

        try {
          this._map.fitBounds(
            geoJsonBounds,
            {
              animate: !this.reducedMotion,
            },
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(e);
        }
      }),
    ).subscribe();
  }

  /**
   * Highlight map features according to the highlighted feature
   *
   * @private
   */
  _updateHighlightedMapFeature() {
    const highlightIds = [...new Set([String(this._selectedFeatureId), String(this._mouseOverFeatureId)])];

    this._map.setPaintProperty(
      'marker',
      'circle-color',
      [
        'match', ['get', 'id'],
        highlightIds, this._mapStyles.markerBgColorHover,
        this._mapStyles.markerBgColor,
      ],
    );
  }

  /**
   * due to loading geojson behaviour, this fix makes sure the order of the layers is respected
   *
   * @param {CustomEvent} event
   */
  _fixLayersPosition(event) {
    const currentLayerId = event.detail;

    const layerElementIds = [...this.querySelectorAll('toujou-map-layer')]
      .map(e => e.getAttribute('layer-id'));

    if (layerElementIds.includes(currentLayerId) === false) {
      return;
    }

    layerElementIds.reverse().forEach((layerId, index) => {
      if (index === 0) {
        return;
      }
      const previousLayerId = layerElementIds[index - 1];

      const currentLayer = this._map.getLayer(layerId);
      const previousLayer = this._map.getLayer(previousLayerId);

      if (currentLayer && previousLayer) {
        this._map.moveLayer(layerId, previousLayerId);
      }
    });
  }
}

customElements.define(ToujouSolrSearchMap.is, ToujouSolrSearchMap);
